import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as I18n from 'circe/I18n';
import Grid from 'pencil/Grid';
import TextField from 'circe/forms/fields/TextField';
import * as Validation from 'circe/forms/validation';
import NumberField from 'circe/forms/fields/NumberField';
import InputAdornment from 'pencil/InputAdornment';
import AddressField from 'circe/forms/fields/AddressField';
import PhoneField from 'circe/forms/fields/PhoneField';

import FileField from 'circe/forms/fields/FileField';
import FieldTitle from 'domain/operations/drivers/create/components/Form/Sections/FieldTitle';
import { isValidPassword, isValidAddress } from 'domain/operations/drivers/create/utils';
import { generateRefId } from 'domain/operations/drivers/apis';
import { useDispatch } from 'redux-react-hook';
import { show as showNotification } from 'circe/notifications/actions';
import Button from 'pencil/Button';
import useCurrentLocation from 'domain/fuse/preferences/location/useCurrentLocation';
import AutoCompleteField from 'domain/finance/fuse/components/AutoCompleteField';
import { isIdNumber } from 'domain/operations/drivers/utils';

const PersonalInformation = (props) => {
  const { companyCodes } = props;
  const [fetchingGeneratedRef, setFetchingGeneratedRef] = React.useState(false);
  const dispatch = useDispatch();
  const [companyCode, setCompanyCode] = useState(null);
  const { country, lat, lng } = useCurrentLocation();
  const classes = {};

  const { name, change } = props;

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={9} sm={9} md={9}>
          <TextField
            fullWidth
            name={`${name}referenceId`}
            autoFocus
            label={I18n.str('referenceId', 'Reference Id')}
            validate={[Validation.required]}
          />
        </Grid>
        <Grid item xs={3} sm={3} md={3}>
          <Button
            loading={fetchingGeneratedRef}
            color="primary"
            variant="contained"
            size="large"
            onClick={async () => {
              try {
                setFetchingGeneratedRef(true);

                const resp = await generateRefId();

                setFetchingGeneratedRef(false);

                if (typeof resp === 'string' || resp instanceof String) {
                  dispatch(change(`${name}referenceId`, resp));
                } else {
                  dispatch(showNotification('unableToGenerateRefId', 'Unable to Generate Ref. Id'));
                }
              } catch (e) {
                console.error(e);
                dispatch(showNotification('unableToGenerateRefId', 'Unable to Generate Ref. Id'));
              }
            }}
          >
            {I18n.str('generateRefId', 'Generate Ref. Id')}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            fullWidth
            name={`${name}firstName`}
            label={I18n.str('firstName', 'First Name')}
            validate={[Validation.required]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            fullWidth
            name={`${name}lastName`}
            label={I18n.str('lastName', 'Last Name')}
            validate={[Validation.required]}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6} md={6}>
          <PhoneField
            name={`${name}phone`}
            validate={[
              Validation.required,
              Validation.validateInternationalPhoneNumber,
              Validation.validateSaudiPhone,
            ]}
            regions={['middle-east']}
            defaultCountry="sa"
            countryCodeEditable={false}
            preferredCountries={['sa']}
            label={I18n.str('phoneNumber', 'Phone Number')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            name={`${name}password`}
            fullWidth
            inputProps={{
              form: {
                autocomplete: 'off',
              },
            }}
            type="password"
            label={I18n.str('password', 'Password')}
            validate={[Validation.required, isValidPassword]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <AutoCompleteField
            name={`${name}token`}
            options={companyCodes}
            label={I18n.str('companyCode', 'Company Code')}
            value={companyCode}
            onChange={(value, option) => {
              const { id } = companyCodes.filter((item) => item.value === option)[0];
              setCompanyCode(option);
              dispatch(change('companyId', id));
            }}
            isClearable
            validate={[Validation.required]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            fullWidth
            name={`${name}email`}
            type="email"
            label={I18n.str('emailOptional', 'Email (Optional)')}
          />
        </Grid>
        <Grid xs={6} item>
          <TextField
            fullWidth
            name={`${name}idNumber`}
            autoFocus
            label={I18n.str('idNumber', 'Id Number (Optional)')}
            validate={[isIdNumber]}
          />
        </Grid>
        <Grid xs={6} item>
          <TextField
            fullWidth
            name={`${name}issuingNumber`}
            autoFocus
            label={I18n.str('issueNumber', 'Issue Number (Optional)')}
            validate={[Validation.number, Validation.positiveValue]}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid xs={7} item>
          <FieldTitle title={I18n.str('baseLocation', 'Base Location')} />
          <AddressField
            name={`${name}baseLocation`}
            fullWidth
            label={I18n.str('chooseLocation', 'Choose Location')}
            validate={[Validation.required, isValidAddress]}
            requestFilters={{
              country,
              location: {
                lat,
                lng,
              },
            }}
          />
        </Grid>
        <Grid xs={5} item>
          <FieldTitle title={I18n.str('drivingDistance', 'Driving Distance')} />
          <NumberField
            name={`${name}within`}
            type="number"
            label={I18n.str('within', 'Within')}
            inputClassName={classes.numberInput}
            validate={[Validation.required, Validation.number]}
            innerLabel
            fullWidth
            min={1}
            InputProps={{
              endAdornment: (
                <InputAdornment className={classes.inputAdornment} position="start">
                  {I18n.str('km', 'km')}
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};

PersonalInformation.propTypes = {
  name: PropTypes.string,
};

PersonalInformation.defaultProps = {
  name: '',
};

export default PersonalInformation;
